import { UpdateUserAction, UsersAdapterActions, UsersLoadedAction } from './../actions/user.actions';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { firstValueFrom, NEVER, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { UserStatus, User } from '../../models/domain';
import { TrackOperations, UserFacadeService, UserService } from '../../services';
import {
  CreateUserAction,
  ResendUserInvitationAction,
  UserActionType,
  UserCreatedAction,
  UserUpdatedAction,
} from '../actions';
import { UserSelectors } from '../selectors';
import { AuthService } from '@anecdotes/auth-core';
import { OperationsTrackerService } from '@anecdotes/shared-infra';

@Injectable()
export class UserEffects {
  constructor(
    private authService: AuthService,
    private usersHttpService: UserService,
    private actions$: Actions,
    private store: Store,
    private operationsTrackerService: OperationsTrackerService,
    private userFacadeService: UserFacadeService
  ) {}

  loadUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActionType.LoadUsers),
      mergeMap(() => this.usersHttpService.getAllUsers()),
      tap((users) => {
        this.operationsTrackerService.trackSuccess(TrackOperations.LOAD_USERS);
        this.userFacadeService.setUsersDictionaryByEmail(users);
      }),
      catchError((err) => {
        this.operationsTrackerService.trackError(TrackOperations.LOAD_USERS, err);
        this.store.dispatch(new UsersLoadedAction([]));
        return NEVER;
      }),
      switchMap(async (allUsers) => {
        const currentUserClaims = await this.authService.getUserAsync();

        // For certain cases current user might not be present in the users list returned from BE
        // however, it's needed
        // For example, in case if current user is SUPPORT_USER, identity does not return it
        // and in order for SUPPORT_USER to be able to use the app without errors, the user should be added to the list
        if (!allUsers.some((user) => user.uid === currentUserClaims.user_id)) {
          const currentUser: User = {
            uid: currentUserClaims.user_id,
            first_name: currentUserClaims.first_name,
            last_name: currentUserClaims.last_name,
            role: currentUserClaims.role,
            email: currentUserClaims.email,
            status: UserStatus.ACTIVE,
            full_name: `${currentUserClaims.first_name} ${currentUserClaims.last_name}`,
            has_reviews: false,
            scim_provisioned: false,
            email_login_disabled: false,
            photo: currentUserClaims.picture,
          };

          return [...allUsers, currentUser];
        }

        return allUsers;
      }),
      map((users) => new UsersLoadedAction(users))
    );
  });

  removeSpecificUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersAdapterActions.removeUser),
      mergeMap((action) =>
        this.usersHttpService.removeSpecificUser(action.payload.email).pipe(
          map(() => UsersAdapterActions.userRemovedSuccessfully({ email: action.payload.email })),
          catchError(() => of(UsersAdapterActions.userRemovalFailed({ email: action.payload.email }))),
        ),
      ),
    ),
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionType.CreateUser),
      mergeMap((action: CreateUserAction) => {
        return this.usersHttpService.createNewUser(action.payload.user, action.payload.notification).pipe(
          map((createdUser) => {
            this.operationsTrackerService.trackSuccess(TrackOperations.CREATE_USER);
            return new UserCreatedAction(createdUser);
          }),
          catchError((err) => {
            this.operationsTrackerService.trackError(TrackOperations.CREATE_USER, err);
            return NEVER;
          })
        );
      })
    )
  );

  resendUserInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionType.ResendUserInvitation),
      mergeMap((action: ResendUserInvitationAction) => {
        return this.usersHttpService.updateUser(action.user_email).pipe(
          tap(() => this.operationsTrackerService.trackSuccess(action.user_email, TrackOperations.UPDATE_USER)),
          tap(() => this.store.dispatch(UsersAdapterActions.userInvitationResent({ email: action.user_email }))),
          catchError((err) => {
            this.operationsTrackerService.trackError(action.user_email, new Error(err), TrackOperations.UPDATE_USER);
            return NEVER;
          })
        );
      })
    ),
  { dispatch: false }
  );

  changeRole$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.changeRole),
        mergeMap(async (action) => {
          try {
            const result = await this.updateUserAsync({ email: action.email, role: action.newRoleKey });
            this.store.dispatch(
              UsersAdapterActions.roleChanged({
                email: result.newUser.email,
                newRoleKey: result.newUser.role,
                oldRoleKey: result.userBeforeUpdate.role,
              })
            );
            this.operationsTrackerService.trackSuccess(action.email, UsersAdapterActions.changeRole.type);
          } catch (error) {
            this.operationsTrackerService.trackError(action.email, error, UsersAdapterActions.changeRole.type);
            this.store.dispatch(UsersAdapterActions.roleChangeFailed({ email: action.email, newRoleKey: action.newRoleKey }));
          }
        })
      ),
    { dispatch: false }
  );

  toggleEmailLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersAdapterActions.toggleEmailLogin),
      switchMap(async (action) => {
        const userToUpdate = await firstValueFrom(
          this.store.pipe(select(UserSelectors.SelectUserByEmail(action.email))),
        );
        const emailLoginDisabled = !userToUpdate.email_login_disabled;

        try {
          await this.updateUserAsync({
            email: action.email,
            email_login_disabled: emailLoginDisabled,
          });
          return UsersAdapterActions.emailLoginChangedSuccessfully({
            email: action.email,
            emailLoginDisabled: emailLoginDisabled,
          });
        } catch {
          return UsersAdapterActions.emailLoginChangeFailed({ email: action.email });
        }
      }),
    ),
  );

  changeAuditorPermissions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.changeAuditorPermissions),
        mergeMap(async (action) => {
          try {
            const result = await this.updateUserAsync({ email: action.email, audit_frameworks: action.auditFrameworks });
            this.store.dispatch(
              UsersAdapterActions.auditorPermissionsChanged({
                email: result.newUser.email,
                newAuditFrameworks: result.newUser.audit_frameworks,
                oldAuditFrameworks: result.userBeforeUpdate.audit_frameworks
              })
            );
            this.operationsTrackerService.trackSuccess(action.email, UsersAdapterActions.changeRole.type);
          } catch (error) {
            this.operationsTrackerService.trackError(action.email, error, UsersAdapterActions.changeRole.type);
            this.store.dispatch(
              UsersAdapterActions.auditorPermissionsChangeFailed({
                email: action.email,
                newAuditFrameworks: action.auditFrameworks,
              }),
            );
          }
        })
      ),
    { dispatch: false }
  );

  updateUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionType.UpdateUser),
        mergeMap(async (action: UpdateUserAction) => {
          try {
            await this.updateUserAsync(action.user);
            this.operationsTrackerService.trackSuccess(action.user.email, TrackOperations.UPDATE_USER);
          } catch (error) {
            this.operationsTrackerService.trackError(action.user.email, error, TrackOperations.UPDATE_USER);
          }
        })
      ),
    { dispatch: false }
  );

  private async updateUserAsync(updatedUser: User): Promise<{ newUser: User; userBeforeUpdate: User }> {
    this.store.dispatch(new UserUpdatedAction(updatedUser));
    const userBeforeUpdate = await firstValueFrom(
      this.store.select(UserSelectors.SelectUserByEmail(updatedUser.email))
    );

    try {
      await firstValueFrom(this.usersHttpService.patchUser(updatedUser));
      return { newUser: updatedUser, userBeforeUpdate };
    } catch (error) {
      this.store.dispatch(new UserUpdatedAction(userBeforeUpdate));
      throw error;
    }
  }
}
